import dayjs from "dayjs";
import { Shift } from "@domain/CRM/shift";
import { Filter, StandardAxiosConfig, getBaseDataURL } from "../../../common";
import { apiClient } from "../../../client";
import { CreateAPI_Base, DataAPI_Base, UpdateAPI_Base } from "../base";

const FOUR_MONTHS_MS = 4 * 30 * 24 * 60 * 60 * 1000;

/** AT "Sched Date" is a date-only column, so it must cross the wire as the LOCAL calendar
 *  date. Left as a Date, axios toJSON()s it to a UTC instant and the API truncates the UTC
 *  day — east-of-UTC users lose a day (MSK: Jul 10 00:00 → "…07-09T21:00Z" → Jul 9). */
const toWire = (shift: Partial<Shift>): Partial<Shift> =>
	shift.scheduledDate
		? ({ ...shift, scheduledDate: dayjs(shift.scheduledDate).format("YYYY-MM-DD") } as unknown as Partial<Shift>)
		: shift;

class ShiftsCreateAPI extends CreateAPI_Base<Shift> {
	override many(records: Partial<Shift>[]): Promise<Shift[]> {
		return super.many(records.map(toWire));
	}
}

class ShiftsUpdateAPI extends UpdateAPI_Base<Shift> {
	override many(records: Shift[], triggerNotesProcessing?: boolean): Promise<Shift[]> {
		return super.many(records.map(toWire) as Shift[], triggerNotesProcessing);
	}
}

export class ShiftsAPI extends DataAPI_Base<Shift> {
	constructor() {
		super("shifts", Shift);
		this.create = new ShiftsCreateAPI("shifts");
		this.update = new ShiftsUpdateAPI("shifts");
	}

	public async byRobotName(robotName: string): Promise<Shift[]> {
		const cutoff = new Date(Date.now() - FOUR_MONTHS_MS).toISOString();
		const filter: Filter<Shift> = {
			type: "AND",
			fields: {
				robotId: robotName,
				startDate: { operator: ">=", value: cutoff },
			},
		};
		const shifts = await this.get.byFilter(filter);
		return shifts.sort((a, b) => (b.startDate?.getTime() ?? 0) - (a.startDate?.getTime() ?? 0));
	}

	/** Boundary Acres of the given jobs (a shift's `jobIds`), keyed by job id, for summing per shift. Unknown ids are
	 *  left out. Served by the jobs router, which owns the jobs table. */
	public async jobAcres(jobIds: string[]): Promise<Record<string, number>> {
		if (jobIds.length === 0) return {};
		const response = await apiClient.post<Record<string, number>>(
			`${getBaseDataURL("jobs")}/get-acres`,
			{ job_ids: jobIds },
			StandardAxiosConfig(),
		);
		if (response.status !== 200) throw new Error("Failed to get job acres");
		return response.data;
	}
}
